import { useCallback, useEffect, useMemo, useState } from "react";
import { _get, _post } from "../../../libraries/fetch";
import {
  createDesktopCredentialURL,
  getDesktopCredentialURL
} from "../../../libraries/apiUrl";
import { copyToClipboard, showToast } from "../../../libraries/clipboardUtils";

const panelStyle = {
  backgroundColor: "#2a2d35",
  border: "1px solid #444",
  borderRadius: "12px",
  padding: "clamp(16px, 3vw, 25px) clamp(14px, 3vw, 30px)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
};

const labelStyle = {
  display: "block",
  marginBottom: "10px",
  fontSize: "15px",
  fontWeight: 600,
  color: "#fff"
};

const inputStyle = {
  width: "100%",
  padding: "12px 15px",
  fontSize: "16px",
  backgroundColor: "#1e2127",
  border: "1px solid #444",
  borderRadius: "8px",
  color: "#fff",
  outline: "none",
  boxSizing: "border-box"
};

const primaryButtonStyle = {
  padding: "12px 28px",
  fontSize: "16px",
  fontWeight: 600,
  backgroundColor: "#187718",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer"
};

const actionButtonStyle = {
  backgroundColor: "transparent",
  border: "1px solid #187718",
  cursor: "pointer",
  padding: "8px 16px",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  justifyContent: "center",
  borderRadius: "6px",
  transition: "all 0.2s",
  fontSize: "13px",
  color: "#187718",
  fontWeight: 600
};

function DesktopCredential({ branchId, branch = null }) {
  const [desktopCredential, setDesktopCredential] = useState("");
  const [desktopCredentialCreatedAt, setDesktopCredentialCreatedAt] = useState(null);
  const [isLoadingDesktopCredential, setIsLoadingDesktopCredential] = useState(false);
  const [isCreatingDesktopCredential, setIsCreatingDesktopCredential] = useState(false);
  const [desktopCredentialError, setDesktopCredentialError] = useState("");
  const [copiedDesktopCredential, setCopiedDesktopCredential] = useState(false);

  const normalizedBranchId = useMemo(() => resolveBranchId(branchId, branch), [branchId, branch]);

  const fetchDesktopCredential = useCallback(async () => {
    if (!normalizedBranchId) {
      setDesktopCredential("");
      setDesktopCredentialCreatedAt(null);
      return;
    }

    setIsLoadingDesktopCredential(true);
    setDesktopCredentialError("");

    try {
      const response = await _get(getDesktopCredentialURL(normalizedBranchId), { useCache: false });

      if (response?.success === false || response?.status === false) {
        throw new Error(getApiResponseMessage(response) || "Failed to load desktop credential.");
      }

      if (response === null || !response?.credential) {
        setDesktopCredential("");
        setDesktopCredentialCreatedAt(null);
        return;
      }

      setDesktopCredential(String(response.credential));
      setDesktopCredentialCreatedAt(response.createdAt || null);
    } catch (error) {
      const errorMessage = error.message || "Failed to load desktop credential.";
      setDesktopCredential("");
      setDesktopCredentialCreatedAt(null);
      setDesktopCredentialError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setIsLoadingDesktopCredential(false);
    }
  }, [normalizedBranchId]);

  useEffect(() => {
    fetchDesktopCredential();
  }, [fetchDesktopCredential]);

  const handleCreateDesktopCredential = async () => {
    if (!normalizedBranchId || isCreatingDesktopCredential) {
      return;
    }

    setIsCreatingDesktopCredential(true);
    setDesktopCredentialError("");

    try {
      const response = await _post(createDesktopCredentialURL(normalizedBranchId), {});

      if (response?.success === false || response?.status === false) {
        throw new Error(getApiResponseMessage(response) || "Failed to create desktop credential.");
      }

      showToast(response?.message || "Desktop credential created successfully", "success");
      await fetchDesktopCredential();
    } catch (error) {
      const errorMessage = error.message || "Failed to create desktop credential.";
      setDesktopCredentialError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setIsCreatingDesktopCredential(false);
    }
  };

  const handleCopyDesktopCredential = async (e) => {
    if (e) {
      e.stopPropagation();
    }

    const result = await copyToClipboard(desktopCredential || "", { label: "Desktop credential" });
    if (result.success) {
      showToast(result.message, "success");
      setCopiedDesktopCredential(true);
      setTimeout(() => {
        setCopiedDesktopCredential(false);
      }, 2000);
    } else {
      showToast(result.message || "Failed to copy desktop credential", "error");
    }
  };

  const handleDownloadDesktopCredential = (e) => {
    if (e) {
      e.stopPropagation();
    }

    if (!desktopCredential) {
      showToast("No desktop credential to download", "error");
      return;
    }

    try {
      const blob = new Blob([desktopCredential], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = "agent.credential";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      showToast("Desktop credential downloaded", "success");
    } catch (error) {
      showToast(error.message || "Failed to download desktop credential", "error");
    }
  };

  return (
    <div className="credential-panel" style={panelStyle}>
      <h2 style={{ margin: "0 0 18px", color: "#fff", fontSize: "18px" }}>Desktop Credential</h2>

      {isLoadingDesktopCredential ? (
        <div style={{ color: "#ccc", padding: "8px 0" }}>Loading...</div>
      ) : desktopCredential ? (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
              gap: "12px",
              flexWrap: "wrap"
            }}
          >
            <label style={{ ...labelStyle, marginBottom: 0 }}>Credential</label>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
              <button
                type="button"
                onClick={handleCopyDesktopCredential}
                style={actionButtonStyle}
                title="Copy desktop credential"
              >
                {copiedDesktopCredential ? "Copied!" : "Copy"}
              </button>
              <button
                type="button"
                onClick={handleDownloadDesktopCredential}
                style={actionButtonStyle}
                title="Download desktop credential"
              >
                Download
              </button>
            </div>
          </div>
          <input
            type="text"
            readOnly
            value={desktopCredential}
            style={{
              ...inputStyle,
              fontFamily: "monospace",
              fontWeight: 600,
              letterSpacing: "0.5px",
              cursor: "default"
            }}
          />
          {desktopCredentialCreatedAt && (
            <div style={{ color: "#888", fontSize: "13px", marginTop: "10px" }}>
              Created: {new Date(desktopCredentialCreatedAt).toLocaleString()}
            </div>
          )}
        </div>
      ) : (
        <div>
          {desktopCredentialError ? (
            <div
              style={{
                color: "#ff8a8a",
                backgroundColor: "#1e2127",
                border: "1px solid #444",
                borderRadius: "8px",
                padding: "14px",
                marginBottom: "16px"
              }}
            >
              {desktopCredentialError}
            </div>
          ) : (
            <div
              style={{
                color: "#ccc",
                backgroundColor: "#1e2127",
                border: "1px solid #444",
                borderRadius: "8px",
                padding: "14px",
                marginBottom: "16px"
              }}
            >
              No desktop credential found for this branch.
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              onClick={handleCreateDesktopCredential}
              disabled={
                isCreatingDesktopCredential ||
                isLoadingDesktopCredential ||
                !normalizedBranchId
              }
              style={{
                ...primaryButtonStyle,
                width: "fit-content",
                opacity:
                  isCreatingDesktopCredential ||
                  isLoadingDesktopCredential ||
                  !normalizedBranchId
                    ? 0.7
                    : 1,
                cursor:
                  isCreatingDesktopCredential ||
                  isLoadingDesktopCredential ||
                  !normalizedBranchId
                    ? "not-allowed"
                    : "pointer"
              }}
            >
              {isCreatingDesktopCredential ? "Creating..." : "Create Desktop Credential"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const getNormalizedId = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "object") {
    return String(value._id || value.id || value.branchId || "").trim();
  }

  return String(value).trim();
};

const resolveBranchId = (branchId, branch) => {
  return getNormalizedId(
    branchId ||
      branch?._id ||
      branch?.id ||
      branch?.branchId ||
      branch?.branch_id ||
      branch?.branchID
  );
};

const getApiResponseMessage = (response) => {
  if (typeof response === "string") {
    return response.trim();
  }

  const message =
    response?.message ||
    response?.data?.message ||
    response?.response?.message ||
    response?.error ||
    response?.data?.error ||
    response?.response?.error;

  return typeof message === "string" ? message.trim() : "";
};

export default DesktopCredential;
